import React, { useCallback, useState } from 'react';
import Switch from '../../../../components/Switch';
import { CHANNELS_CATALOG } from '../../../../utils/channelsConfig';
import ModulesPicker, { mergeModules } from './ModulesPicker';
import ClientMenuPreview from './ClientMenuPreview';
import QaProfileConfig from '../QaProfileConfig/QaProfileConfig';
import InsightsConfig from '../InsightsConfig/InsightsConfig';
import DeliveryConfig from '../DeliveryConfig/DeliveryConfig';

const C = { line: 'rgba(255,255,255,0.07)', text: '#E8EAF0', muted: '#8A90AD', faint: '#5A6080' };
const on = (v) => v === true || v === 'true';

const CHANNEL_HELP = {
  whatsapp_call: 'Llamadas de voz por WhatsApp',
  phone_call: 'Llamadas a número telefónico',
};

function Section({ id, title, intro, children, locked }) {
  return (
    <section aria-labelledby={id} className="pt-8 border-t first:pt-0 first:border-t-0" style={{ borderColor: C.line }}>
      <h3 id={id} className="text-base font-semibold" style={{ color: C.text }}>{title}</h3>
      <p className="text-[13px] mt-1 mb-4 max-w-[68ch]" style={{ color: C.muted }}>{intro}</p>
      {locked ? (
        <p className="rounded-xl px-4 py-3 text-sm" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: `1px dashed ${C.line}`, color: C.muted }}>
          {locked}
        </p>
      ) : children}
    </section>
  );
}

/** Paso "Permisos" del proyecto: qué ve el cliente, por dónde atiende el agente y cómo se mide. */
export default function PermisosStep({ proyect, setProyect, projectId, agentType }) {
  const [profileName, setProfileName] = useState(null);
  const modules = mergeModules(proyect.modules);
  const intel = modules.find((m) => m.module === 'inteligencia');
  const envios = modules.find((m) => m.module === 'envios');
  const qaOn = !!(intel?.view && intel?.special_actions?.can_view_qa);
  const channels = proyect.channels?.[0] || {};
  const onProfileChange = useCallback((name) => setProfileName(name), []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-8 xl:gap-12">
      <div className="space-y-8 min-w-0">
        <Section
          id="perm-modulos" title="Qué puede ver y hacer el cliente"
          intro="Cada módulo es una sección del dashboard del cliente. Actívalo para que aparezca en su menú y elige qué puede hacer dentro."
        >
          <ModulesPicker
            modules={proyect.modules}
            onChange={(updated) => setProyect((prev) => ({ ...prev, modules: updated }))}
          />
        </Section>

        <Section
          id="perm-canales" title="Por dónde atiende el agente"
          intro="Los canales en los que el agente conversa con los clientes. Sus conversaciones aparecen en Chats y se analizan en Calidad y en los avisos."
        >
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
            {CHANNELS_CATALOG.map((c) => {
              const id = `chan-${c.key}`;
              return (
                <li key={c.key} className="flex items-start gap-3 py-2">
                  <Switch
                    id={id} checked={on(channels[c.key])} label={c.label}
                    onChange={(v) => setProyect((prev) => ({ ...prev, channels: [{ ...(prev.channels?.[0] || {}), [c.key]: v }] }))}
                  />
                  <label htmlFor={id} className="text-sm cursor-pointer" style={{ color: C.text }}>
                    {c.label}
                    {CHANNEL_HELP[c.key] && <span className="block text-xs" style={{ color: C.muted }}>{CHANNEL_HELP[c.key]}</span>}
                  </label>
                </li>
              );
            })}
          </ul>
        </Section>

        <Section
          id="perm-calidad" title="Cómo medimos a este agente"
          intro="El objetivo del agente y las etapas de su embudo. Con esto leemos cada conversación para saber cuántas cumplen el objetivo, dónde se pierde la atención del cliente y por qué."
          locked={!qaOn && 'Para medirlo, activa Inteligencia y su permiso “Calidad de atención” en la lista de módulos.'}
        >
          <QaProfileConfig projectId={projectId} agentType={agentType} onProfileChange={onProfileChange} />
        </Section>

        <Section
          id="perm-avisos" title="Avisos de IA"
          intro="Qué cambios en los datos le avisamos al cliente, qué tan sensibles son los avisos y por dónde le llegan."
          locked={!intel?.view && 'Activa el módulo Inteligencia en la lista de arriba para configurar los avisos.'}
        >
          {projectId ? (
            <InsightsConfig projectId={projectId} />
          ) : (
            <p className="text-sm" style={{ color: C.muted }}>Guarda el proyecto para configurar sus avisos.</p>
          )}
        </Section>

        <Section
          id="perm-envios" title="Canales de envío"
          intro="El correo y el WhatsApp con que la plataforma envía los avisos al cliente. El cliente también puede configurarlos en su dashboard, en Envíos. Los correos que envíe el agente se manejan aparte, en n8n."
          locked={!envios?.view && 'Activa el módulo “Canales de envío” en la lista de arriba para configurarlos.'}
        >
          <DeliveryConfig projectId={projectId} />
        </Section>
      </div>

      <div className="lg:sticky lg:top-28 self-start order-first lg:order-none">
        <ClientMenuPreview
          modules={modules} channels={proyect.channels}
          profileName={profileName} agentTypeName={agentType?.name}
        />
      </div>
    </div>
  );
}
